import type { Property } from 'csstype';
import type { NodeEntry, Path } from 'slate';
import { Editor, Range, Text } from 'slate';

import type {
  DrawAnnotationMarkerInstruction,
  DrawCaretInstruction,
  DrawErrorInstruction,
  DrawImageInstruction,
  DrawInstruction,
  DrawLineInstruction,
  DrawPDFBookmarkInstruction,
  DrawPDFLinkInstruction,
  DrawReactComponentInstruction,
  DrawReactComponentName,
  DrawReactComponentProps,
  DrawRectInstruction,
  DrawSpellcheckUnderlineInstruction,
  DrawTextInstruction,
} from './draw-instruction';
import { getRenderChildState } from './rendering-data';
import type { BaseRenderNodeOptions, RenderNodeState, StaticRenderingData } from './rendering-data';
import { isRenderModelEmptySplitNodeText, isRenderModelLineEntry, Queries } from '../../slate-api';
import type {
  NodeBoundingBox,
  RenderModelContentBlock,
  RenderModelDescendant,
  RenderModelElement,
  RenderModelFooter,
  RenderModelHeader,
  RenderModelPage,
  RenderModelText,
} from '../../slate-api';
import { arrayEntries } from '../../util';

export interface RenderNodeInstruction<
  N extends RenderModelDescendant,
  O extends BaseRenderNodeOptions,
> {
  boundingBox: NodeBoundingBox<N>;
  options: O;
  renderFn: RenderNodeFn<N, O>;
  renderNodeState: RenderNodeState;
  type: 'renderNode';
}

export type RenderInstruction =
  | DrawInstruction
  | RenderNodeInstruction<RenderModelDescendant, BaseRenderNodeOptions>;

/** The render function for a given node type. */
export type RenderNodeFn<N extends RenderModelDescendant, O extends BaseRenderNodeOptions> = (
  renderContext: RenderContext,
  rMNodeEntry: NodeEntry<N>,
  options: O
) => void;

export type RenderTextFn<O extends BaseRenderNodeOptions> = RenderNodeFn<RenderModelText, O>;

/** An object containing a render function for each possible element type. */
export type RenderElementFnsOf<N extends RenderModelDescendant, O extends BaseRenderNodeOptions> = {
  [T in Extract<N, RenderModelElement>['type']]: RenderNodeFn<
    Extract<RenderModelElement, { type: T }>,
    O
  >;
};

/**
 * An object containing a render function for each possible node type, where text nodes have a type
 * of 'text'.
 */
export type RenderNodeFnsOf<
  Ns extends RenderModelDescendant,
  O extends BaseRenderNodeOptions,
> = RenderModelText extends Ns
  ? RenderElementFnsOf<Ns, O> & { text: RenderTextFn<O> }
  : RenderElementFnsOf<Ns, O>;

/** A selector function on the editor state. */
export type EditorSelector<T> = (editor: Editor) => T;

/** A selector function on some value in the static rendering data. */
export type StaticRenderingDataSelector<K extends keyof StaticRenderingData, T> = (
  value: StaticRenderingData[K]
) => T;

/** An editor selector function and its cached result, used for cache invalidation. */
export interface EditorSelectorInfo {
  fn: EditorSelector<unknown>;
  result: unknown;
}

/**
 * A static rendering data selector, optionally including a function and its cached result, used for
 * cache invalidation.
 */
export interface StaticRenderingDataSelectorInfo {
  key: keyof StaticRenderingData;
  fn?: StaticRenderingDataSelector<keyof StaticRenderingData, unknown>;
  fnResult?: unknown;
}

/**
 * Helper object that gathers rendering information for a single node.
 */
export class RenderContext {
  editor: Editor;

  private staticRenderingData: StaticRenderingData;
  private renderNodeState: RenderNodeState;
  private renderInstructions: RenderInstruction[] = [];
  private editorSelectors: EditorSelectorInfo[] = [];
  private staticRenderingDataSelectors: StaticRenderingDataSelectorInfo[] = [];
  private cachedCMPath?: Path | null;

  constructor(
    editor: Editor,
    options: { renderNodeState: RenderNodeState; staticRenderingData: StaticRenderingData }
  ) {
    const { renderNodeState, staticRenderingData } = options;
    this.editor = editor;
    this.staticRenderingData = staticRenderingData;
    this.renderNodeState = renderNodeState;
  }

  /**
   * Render a node using this render context.
   */
  renderNode<N extends RenderModelDescendant, O extends BaseRenderNodeOptions>(
    boundingBox: NodeBoundingBox<N>,
    renderFn: RenderNodeFn<N, O>,
    options: O
  ) {
    const { heightPx, rMNodeEntry, widthPx } = boundingBox;
    const [rMNode, rMPath] = rMNodeEntry;
    const suggestedEdits = rMNode.suggestedEdits ?? [];

    try {
      renderFn(this, rMNodeEntry, options);
      if (suggestedEdits?.length) this.drawAnnotationMarker({ suggestedEdits });
    } catch (error) {
      console.error(error);

      // Clear any already rendered instructions
      this.renderInstructions = [];
      this.editorSelectors = [];
      this.staticRenderingDataSelectors = [];

      this.drawError(0, 0, widthPx, heightPx, { rMPath });
    }

    return {
      editorSelectors: this.editorSelectors,
      renderInstructions: this.renderInstructions,
      staticRenderingDataSelectors: this.staticRenderingDataSelectors,
    };
  }

  /**
   * Retrieve a value from the static rendering data. The node will automatically re-render whenever
   * this value changes.
   */
  withContext<K extends keyof StaticRenderingData>(key: K): StaticRenderingData[K] {
    this.staticRenderingDataSelectors.push({ key });
    return this.staticRenderingData[key];
  }

  /**
   * Retrieve information from the static rendering data using a selector. The node will
   * automatically re-render whenever the result of this selector changes.
   */
  withContextSelector<K extends keyof StaticRenderingData, T>(
    key: K,
    fn: StaticRenderingDataSelector<K, T>
  ): T {
    const result = fn(this.staticRenderingData[key]);
    this.staticRenderingDataSelectors.push({
      fn: fn as StaticRenderingDataSelector<keyof StaticRenderingData, unknown>,
      fnResult: result,
      key,
    });
    return result;
  }

  /**
   * Query the editor using a selector. The node will automatically re-render whenever the result of
   * this selector changes.
   */
  withEditor<T>(fn: EditorSelector<T>): T {
    const result = fn(this.editor);
    this.editorSelectors.push({ fn, result });
    return result;
  }

  get clipRect() {
    return this.renderNodeState.clipRect;
  }

  get isLastNodeInLine() {
    return this.renderNodeState.isLastNodeInLine;
  }

  get offsetX() {
    return this.renderNodeState.offsetX;
  }

  get offsetY() {
    return this.renderNodeState.offsetY;
  }

  get rMPath() {
    return this.renderNodeState.rMPath;
  }

  get rMDropPoint() {
    return this.renderNodeState.rMDropPoint;
  }

  get rMRemoteSelIntersections() {
    return this.renderNodeState.rMRemoteSelIntersections;
  }

  get rMSelIntersection() {
    return this.renderNodeState.rMSelIntersection;
  }

  get rMRange() {
    return this.rMPath && Queries.renderModelRange(this.editor, this.rMPath);
  }

  get cMPath(): Path | null {
    if (this.cachedCMPath === undefined)
      this.cachedCMPath = this.rMPath && Queries.getCMPathFromRMPath(this.editor, this.rMPath);
    return this.cachedCMPath;
  }

  get cMRange() {
    if (!this.cMPath) return null;

    if (!Editor.hasPath(this.editor, this.cMPath)) {
      console.error('Stale cMPath:', this.cMPath);
      return null;
    }

    return Editor.range(this.editor, this.cMPath);
  }

  /**
   * Check if the selection is entirely within the content model node.
   */
  isSelectionInsideCMNode() {
    if (
      this.withContextSelector('gridSelectionInfo', gridSelectionInfo => gridSelectionInfo !== null)
    )
      return false;

    return this.withEditor(editor => {
      if (!editor.selection || !this.cMRange) return false;
      return Range.surrounds(this.cMRange, editor.selection);
    });
  }

  /**
   * Render the children of a render model element using the given render functions.
   *
   * If you require more control over how each individual child is rendered, compute the bounding
   * boxes yourself and call renderChild instead.
   *
   * Rendering the children of the page is a special case, since the bounding boxes of a page's
   * children include the header and footer for that page, even though they're not direct children
   * of the page.
   */
  renderChildren<N extends RenderModelElement, O extends BaseRenderNodeOptions>(
    rMNodeEntry: NodeEntry<N>,
    renderFns: N extends RenderModelPage
      ? RenderNodeFnsOf<RenderModelContentBlock | RenderModelHeader | RenderModelFooter, O>
      : RenderNodeFnsOf<N['children'][number], O>,
    options: O
  ) {
    const parentIsLine = isRenderModelLineEntry(rMNodeEntry);
    const boundingBoxes = Queries.getRMBoundingBoxes(this.editor, rMNodeEntry);

    for (const [index, boundingBox] of arrayEntries(boundingBoxes)) {
      if (boundingBox.type !== 'node') continue;

      this.renderChild(
        boundingBox,
        renderFns as RenderNodeFnsOf<RenderModelDescendant, O>,
        options,
        { isLastChild: index === boundingBoxes.length - 1, parentIsLine }
      );
    }
  }

  /**
   * Render a child of the current node using its bounding box and the given set of render functions
   * for each possible child type.
   */
  renderChild<N extends RenderModelDescendant, O extends BaseRenderNodeOptions>(
    boundingBox: NodeBoundingBox<N>,
    renderFns: RenderNodeFnsOf<N, O>,
    childOptions: O,
    options: { isLastChild: boolean; parentIsLine?: boolean }
  ) {
    const { rMNodeEntry } = boundingBox;
    const [rMNode] = rMNodeEntry;
    const { isLastChild, parentIsLine = false } = options;
    if (isRenderModelEmptySplitNodeText(rMNode)) return;

    const type = Text.isText(rMNode) ? 'text' : rMNode.type;
    const renderFn = Object.hasOwn(renderFns, type)
      ? renderFns[type as keyof typeof renderFns]
      : undefined;

    if (!renderFn) {
      console.error(`Unexpected child type: ${type}`);
      return;
    }

    this.renderInstructions.push({
      boundingBox,
      options: childOptions,
      renderFn: renderFn as RenderNodeFn<RenderModelDescendant, BaseRenderNodeOptions>,
      renderNodeState: getRenderChildState(this.editor, this.renderNodeState, {
        boundingBox,
        isLastChild,
        parentIsLine,
      }),
      type: 'renderNode',
    });
  }

  /**
   * Render something other than a node at a given (x, y) position. To avoid accidentally accessing
   * node-related state pertaining to the parent node, this state is set to null. The main purpose
   * of this is to safely call renderText from within the render function.
   */
  renderSpecial(x: number, y: number, renderFn: () => void) {
    const { renderNodeState } = this;

    this.renderNodeState = {
      clipRect: this.clipRect,
      isLastNodeInLine: null,
      offsetX: this.offsetX + x,
      offsetY: this.offsetY + y,
      rMDropPoint: null,
      rMPath: null,
      rMRemoteSelIntersections: null,
      rMSelIntersection: null,
    };

    try {
      renderFn();
    } finally {
      this.renderNodeState = renderNodeState;
    }
  }

  /**
   * Clip all supported draw instructions by a given rectangle.
   */
  withClipRect(x: number, y: number, width: number, height: number, renderFn: () => void) {
    const previousClipRect = this.clipRect;

    const {
      height: previousHeight = null,
      width: previousWidth = null,
      x: previousStartX = -Infinity,
      y: previousStartY = -Infinity,
    } = previousClipRect ?? {};

    const previousEndX = previousWidth === null ? Infinity : previousStartX + previousWidth;
    const previousEndY = previousHeight === null ? Infinity : previousStartY + previousHeight;

    const startX = Math.max(previousStartX, this.offsetX + x);
    const startY = Math.max(previousStartY, this.offsetY + y);
    const endX = Math.min(previousEndX, this.offsetX + x + width);
    const endY = Math.min(previousEndY, this.offsetY + y + height);

    this.renderNodeState.clipRect = {
      height: endY - startY,
      width: endX - startX,
      x: startX,
      y: startY,
    };

    try {
      renderFn();
    } finally {
      this.renderNodeState.clipRect = previousClipRect;
    }
  }

  drawText(
    x: number,
    y: number,
    text: string,
    options: Omit<DrawTextInstruction, 'type' | 'x' | 'y' | 'text' | 'clipRect'>
  ) {
    this.renderInstructions.push({
      clipRect: this.clipRect,
      text,
      type: 'text',
      x: x + this.offsetX,
      y: y + this.offsetY,
      ...options,
    });
  }

  drawRect(
    x: number,
    y: number,
    width: number,
    height: number,
    options: Omit<DrawRectInstruction, 'type' | 'x' | 'y' | 'width' | 'height' | 'clipRect'>
  ) {
    this.renderInstructions.push({
      clipRect: this.clipRect,
      height,
      type: 'rect',
      width,
      x: x + this.offsetX,
      y: y + this.offsetY,
      ...options,
    });
  }

  drawHorizontalLine({
    y,
    ...options
  }: Omit<DrawLineInstruction, 'type' | 'startY' | 'endY' | 'direction' | 'clipRect'> & {
    y: number;
  }) {
    this.drawLine({
      clipRect: this.clipRect,
      direction: 'horizontal',
      endY: y,
      startY: y,
      ...options,
    });
  }

  drawVerticalLine({
    clippable = true,
    x,
    ...options
  }: Omit<DrawLineInstruction, 'type' | 'startX' | 'endX' | 'direction' | 'clipRect'> & {
    x: number;
    clippable?: boolean;
  }) {
    this.drawLine({
      clipRect: clippable ? this.clipRect : null,
      direction: 'vertical',
      endX: x,
      startX: x,
      ...options,
    });
  }

  drawLineSegment(
    options: Omit<DrawLineInstruction, 'type' | 'clipRect' | 'direction'> &
      Partial<Pick<DrawLineInstruction, 'direction'>>
  ) {
    const { direction = 'diagonal', ...lineOptions } = options;
    this.drawLine({
      clipRect: this.clipRect,
      direction,
      ...lineOptions,
    });
  }

  drawSpellcheckUnderline({
    x,
    y,
    ...options
  }: Omit<DrawSpellcheckUnderlineInstruction, 'type' | 'clipRect'>) {
    this.renderInstructions.push({
      clipRect: this.clipRect,
      type: 'spellcheckUnderline',
      x: x + this.offsetX,
      y: y + this.offsetY,
      ...options,
    });
  }

  drawImage(
    x: number,
    y: number,
    width: number,
    height: number,
    options: Omit<DrawImageInstruction, 'type' | 'x' | 'y' | 'width' | 'height' | 'clipRect'>
  ) {
    this.renderInstructions.push({
      clipRect: this.clipRect,
      height,
      type: 'image',
      width,
      x: x + this.offsetX,
      y: y + this.offsetY,
      ...options,
    });
  }

  drawCaret(x: number, y: number, options: Omit<DrawCaretInstruction, 'type' | 'x' | 'y'>) {
    this.renderInstructions.push({
      type: 'caret',
      x: x + this.offsetX,
      y: y + this.offsetY,
      ...options,
    });
  }

  drawAnnotationMarker(
    options: Partial<Omit<DrawAnnotationMarkerInstruction, 'type' | 'y' | 'cMRange'>>
  ) {
    if (!this.cMRange) return;
    this.renderInstructions.push({
      cMRange: this.cMRange,
      commentIds: [],
      suggestedEdits: [],
      type: 'annotationMarker',
      y: this.offsetY,
      ...options,
    });
  }

  drawPDFLink(
    x: number,
    y: number,
    width: number,
    height: number,
    options: Omit<DrawPDFLinkInstruction, 'type' | 'x' | 'y' | 'width' | 'height'>
  ) {
    this.renderInstructions.push({
      height,
      type: 'pdfLink',
      width,
      x: x + this.offsetX,
      y: y + this.offsetY,
      ...options,
    });
  }

  drawPDFBookmark(
    x: number,
    y: number,
    options: Omit<DrawPDFBookmarkInstruction, 'type' | 'x' | 'y'>
  ) {
    this.renderInstructions.push({
      type: 'pdfBookmark',
      x: x + this.offsetX,
      y: y + this.offsetY,
      ...options,
    });
  }

  renderReactComponent<N extends DrawReactComponentName>(
    x: number,
    y: number,
    componentName: N,
    {
      clippable,
      ...options
    }: Omit<
      DrawReactComponentInstruction,
      'clipRect' | 'componentName' | 'props' | 'type' | 'x' | 'y'
    > & {
      /** Whether the component should respect the current clipping rect. */
      clippable: boolean;
    },
    props: DrawReactComponentProps[N]
  ) {
    this.renderInstructions.push({
      clipRect: clippable ? this.clipRect : null,
      componentName,
      props,
      type: 'reactComponent',
      x: x + this.offsetX,
      y: y + this.offsetY,
      ...options,
    });
  }

  setMouseCursor(
    x: number,
    y: number,
    width: number,
    height: number,
    options: { cursor: Property.Cursor; key: string }
  ) {
    this.renderReactComponent(
      x,
      y,
      'MouseCursorOverlay',
      { clippable: true, key: options.key, zIndex: 1 },
      {
        cursor: options.cursor,
        height,
        width,
      }
    );
  }

  /** A collection of methods used for unit testing render functions. */
  get testing() {
    return {
      getDrawInstruction: <T extends DrawInstruction['type']>(
        type: T,
        options: { debugName?: string } = {}
      ): Extract<DrawInstruction, { type: T }> => {
        const { debugName } = options;
        const matchingDrawInstructions = this.testing.getDrawInstructions(type, options);

        if (matchingDrawInstructions.length !== 1) {
          const qualifier = debugName ? ` with debugName '${debugName}'` : '';
          throw new Error(
            `Expected exactly 1 draw instruction of type ${type + qualifier}, but got ${matchingDrawInstructions.length}`
          );
        }

        return matchingDrawInstructions[0];
      },

      getDrawInstructions: <T extends DrawInstruction['type']>(
        type: T,
        options: { debugName?: string } = {}
      ): Extract<DrawInstruction, { type: T }>[] => {
        const { debugName } = options;

        const matchingDrawInstructions = this.renderInstructions.filter(renderInstruction => {
          if (renderInstruction.type !== type) return false;
          if (debugName && renderInstruction.debugName !== debugName) return false;
          return true;
        });

        return matchingDrawInstructions as Extract<DrawInstruction, { type: T }>[];
      },
    };
  }

  private drawLine({ endX, endY, startX, startY, ...options }: Omit<DrawLineInstruction, 'type'>) {
    this.renderInstructions.push({
      endX: endX + this.offsetX,
      endY: endY + this.offsetY,
      startX: startX + this.offsetX,
      startY: startY + this.offsetY,
      type: 'line',
      ...options,
    });
  }

  private drawError(
    x: number,
    y: number,
    width: number,
    height: number,
    options: Omit<DrawErrorInstruction, 'type' | 'x' | 'y' | 'width' | 'height'>
  ) {
    this.renderInstructions.push({
      height,
      type: 'error',
      width,
      x: x + this.offsetX,
      y: y + this.offsetY,
      ...options,
    });
  }
}
